import React from 'react';

import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import AddNodeHandle from 'ui-library/reactflow/handles/AddNodeHandle';

import {AnalysisFlowchartAction} from '../actions/AnalysisFlowchartActionTypes';
import AnalysisGraphUtils from '../analysis-graph/AnalysisGraphUtils';
import {useAnalysisFlowchartContext} from '../AnalysisFlowchartContext';

enum StepDataRole {
  INPUT,
  OUTPUT,
}

export interface StepDataHandleProps {
  step: PipelineStep;
  /** Step data the handle represents. */
  stepData: PipelineStepData;
}

/**
 * A handle, which represents an input or output step data on a step.
 *
 * In `reactflow`, "handles" are the components rendered where edges and nodes connect.
 */
export default function StepDataHandle({step, stepData}: StepDataHandleProps): JSX.Element {
  // Search by `id` rather than `name`, to not assume input and output step data names are disjoint
  const stepDataRole = step.inputStepDatas.find(({id}) => id === stepData.id)
    ? StepDataRole.INPUT
    : StepDataRole.OUTPUT;

  const {getActionInfo} = useAnalysisFlowchartContext();

  /**
   * Check the `ADD_STEP` action is enabled for all steps whose inputs would be updated by adding a step.
   *
   * This assumes clicking `StepDataHandle` adds a step inline; i.e. any other steps currently connected to
   * `stepData` will be re-connected to the new step. (Adding a step to a new branch wouldn't affect steps
   * currently connected to `stepData`, if it is an output step data.)
   */
  const stepsAddStepActionWouldUpdate =
    stepDataRole === StepDataRole.INPUT
      ? [step]
      : step.outgoingEdges
          .filter(({fromStepData}) => fromStepData.name === stepData.name)
          .map(({toStep}) => toStep);

  const {doAction: openAddStep, enabled: canAddStep} = getActionInfo(
    AnalysisFlowchartAction.OPEN_ADD_STEP,
    ...stepsAddStepActionWouldUpdate.map(({id}) => id)
  );

  const {enabled: isConnectable} = getActionInfo(
    AnalysisFlowchartAction.CONNECT_STEPS,
    ...stepsAddStepActionWouldUpdate.map(({id}) => id)
  );

  return (
    <AddNodeHandle
      id={AnalysisGraphUtils.getHandleId(stepData)}
      type={stepDataRole === StepDataRole.INPUT ? 'target' : 'source'}
      onClick={() => {
        openAddStep?.(step.id, stepData.name);
      }}
      disabled={!canAddStep}
      isConnectable={isConnectable}
    />
  );
}
